import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Avatar,
  Badge,
  Popper,
  Paper,
  ClickAwayListener,
  Grow,
  Button,
  Skeleton,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { PersonOutlined, KeyboardArrowDownOutlined, NotificationsOutlined } from '@mui/icons-material';

import { UserContext, UnreadContext, SnackbarContext } from '../contexts';
import { NotificationsService, AuthenticationService } from '../services';

import Notification from './Notification.jsx';

const UserDropdownMenu = () => {
  const theme = useTheme();
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md'));

  const { currentUser, isSuperAdmin } = useContext(UserContext);
  const { notificationsCount, setNotificationsCount } = useContext(UnreadContext);
  const { setSnackbar, setSnackbarIsOpen } = useContext(SnackbarContext);

  const [anchorEl, setAnchorEl] = useState(null);

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState(undefined);

  const { notifications: notificationsPermission } = currentUser?.permissions?.administration ?? {};

  useEffect(() => {
    if (notificationsPermission?.view || isSuperAdmin) {
      const fetchData = async () => {
        if (AuthenticationService.isLoggedIn()) {
          const data = await NotificationsService.unreadCount();
          setNotificationsCount(data);
        }
      };

      fetchData();
      const interval = setInterval(async () => {
        fetchData();
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isSuperAdmin, notificationsPermission?.view, setNotificationsCount]);

  const handleClose = () => setAnchorEl(null);

  const profileHandler = () => {
    navigate('/user/manage-profile');
    setAnchorEl(null);
  };

  const handleClick = event => setAnchorEl(anchorEl ? null : event.currentTarget);

  const onOpenNotifications = useCallback(() => {
    const fetchData = async () => {
      if (notificationsPermission?.view || isSuperAdmin) {
        try {
          setOpen(prevOpen => !prevOpen);
          setLoading(true);
          setNotifications(await NotificationsService.list(false));
        } catch {
          setSnackbar({ message: translate('snackbarErrorMsg'), severity: 'error' });
          setSnackbarIsOpen(true);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [isSuperAdmin, notificationsPermission?.view, setSnackbar, setSnackbarIsOpen, translate]);

  const handlePopperClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      {notificationsPermission?.view || isSuperAdmin ? (
        <Box>
          <Badge overlap='circular' color='error' badgeContent={notificationsCount} max={9}>
            <IconButton ref={anchorRef} onClick={onOpenNotifications}>
              <NotificationsOutlined
                style={{
                  color: theme.palette?.white?.main,
                }}
              />
            </IconButton>
          </Badge>
          <Popper id='notifications-popper' open={open} anchorEl={anchorRef.current} sx={{ zIndex: 100 }} transition disablePortal role={undefined}>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}>
                <Paper
                  square={false}
                  sx={{
                    width: '100%',
                    minWidth: `min(${theme.spacing(50)}, 95vw)`,
                    maxWidth: `min(${theme.spacing(75)}, 95vw)`,
                    maxHeight: '50vh',
                    overflowY: 'auto',
                    mt: 2,
                  }}>
                  <ClickAwayListener onClickAway={handlePopperClose}>
                    <Stack direction='column' justifyContent='center' alignItems='stretch' spacing={2} py={4} px={2} sx={{ width: '100%' }}>
                      <Stack direction='column' justifyContent='center' alignItems='stretch' spacing={1}>
                        {loading ? (
                          Array.from({ length: 3 }).map((_, index) => (
                            <Paper variant='outlined' square={false} key={index} sx={{ p: 2, gap: 2, display: 'flex', flexDirection: 'column' }}>
                              <Skeleton
                                variant='rectangular'
                                animation='wave'
                                sx={{ width: '100%', borderRadius: theme.spacing(0.25), height: theme.spacing(2) }}
                              />
                              <Skeleton
                                variant='rectangular'
                                animation='wave'
                                sx={{ width: '100%', borderRadius: theme.spacing(0.25), height: theme.spacing(2) }}
                              />
                            </Paper>
                          ))
                        ) : notifications?.length > 0 ? (
                          notifications.map((item, index) => <Notification item={item} key={index} />)
                        ) : (
                          <Typography variant='h6' sx={{ alignSelf: 'center' }}>
                            {translate('NoUnreadNotificationsMessage')}
                          </Typography>
                        )}
                      </Stack>
                      <Button color='info' disabled={loading} href='/notification'>
                        {translate('ViewAllNotificationsLinkText')}
                      </Button>
                    </Stack>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      ) : (
        <></>
      )}
      {!isMobile && (
        <>
          <Avatar sx={{ p: theme.spacing(4), textTransform: 'uppercase' }}>{currentUser?.firstName?.charAt(0)}</Avatar>
          <Box>
            <Typography color={theme.palette.white.main}>
              {`${currentUser?.firstName} ${currentUser?.lastName}`}
              <IconButton onClick={handleClick}>
                <KeyboardArrowDownOutlined
                  style={{
                    color: theme.palette.white.main,
                  }}
                  fontSize='small'
                />
              </IconButton>
            </Typography>
            <Typography color='primary'>{currentUser?.email}</Typography>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={anchorEl ? true : false}
            onClose={handleClose}
            sx={{
              justifyContent: 'center',
            }}>
            <MenuItem onClick={profileHandler}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                }}>
                <PersonOutlined />
                <Typography>{translate('TopBarDropDownManageProfile')}</Typography>
              </Box>
            </MenuItem>
          </Menu>
        </>
      )}
    </>
  );
};

export default UserDropdownMenu;
